import { BlockCtaButtons } from "@/components/block-cta-buttons";
import { Band, Eyebrow, displayFont } from "./band-helpers";
import type { PromoBandBlock } from "@/lib/storefront-types";

type PromoBandSectionProps = {
  block: PromoBandBlock;
};

export function PromoBandSection({ block }: PromoBandSectionProps) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const isDark = !isLight;

  return (
    <Band
      className={
        isLight
          ? "border-y storefront-border-soft bg-[#f7fbf4] py-16 sm:py-20 lg:py-24"
          : isFrozen
            ? "relative overflow-hidden storefront-surface-frozen py-16 sm:py-20 lg:py-24"
            : "relative overflow-hidden storefront-surface-dark-alt py-16 sm:py-20 lg:py-24"
      }
    >
      {isDark ? (
        <div className="storefront-accent-radial pointer-events-none absolute inset-0" />
      ) : null}

      <div className="relative grid gap-10 lg:grid-cols-[1.18fr_0.82fr] lg:items-center">
        <div className="space-y-6">
          {block.eyebrow ? (
            <Eyebrow inverse={isDark}>{block.eyebrow}</Eyebrow>
          ) : null}
          <h2
            className={`${displayFont.className} text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight ${
              isDark ? "text-white" : "storefront-text-heading"
            }`}
          >
            {block.headline}
          </h2>
          <div
            className={`max-w-xl space-y-5 text-lg leading-8 ${
              isDark ? "text-white/66" : "storefront-text-body"
            }`}
            dangerouslySetInnerHTML={{ __html: block.bodyHtml }}
          />
          <BlockCtaButtons
            buttons={block.buttons}
            className={isDark ? "" : "items-center"}
          />
        </div>

        <div
          className={`rounded-[1.6rem] p-6 sm:p-8 ${
            isLight
              ? "border storefront-border-soft bg-white shadow-[0_24px_48px_-34px_rgba(0,39,0,0.22)]"
              : isFrozen
                ? "storefront-border-primary-soft storefront-surface-frozen-soft border backdrop-blur-sm"
                : "border border-white/10 bg-white/6 backdrop-blur-sm"
          }`}
        >
          {block.notesPanelTitle ? (
            <p
              className={`text-sm font-semibold uppercase tracking-[0.2em] ${
                isLight ? "storefront-text-primary" : "storefront-text-primary-tint"
              }`}
            >
              {block.notesPanelTitle}
            </p>
          ) : null}
          <ul className="mt-5 space-y-3" role="list">
            {block.noteItems.map((item) => (
              <li
                key={item.text}
                className={`flex items-start gap-3 text-sm leading-6 ${
                  isLight ? "storefront-text-body" : isFrozen ? "text-white/84" : "text-white/82"
                }`}
              >
                <span className="storefront-bg-primary mt-2 h-1.5 w-1.5 shrink-0 rounded-full" />
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Band>
  );
}
